// ── Types ─────────────────────────────────────────────────────────────────────

export interface TemplatePlant {
  plantId: string;
  plantName: string;
  quantity: number;
  notes: string;
}

export interface GardenTemplate {
  id: string;
  name: string;
  tagline: string;
  description: string;
  category: 'in-ground' | 'raised-bed' | 'container';
  recommendedSize: string;
  difficulty: 'beginner' | 'intermediate';
  season: 'spring' | 'summer' | 'fall' | 'year-round';
  plants: TemplatePlant[];
  tags: string[];
  emoji: string;
  categoryNote?: string;
}

// ── Templates ─────────────────────────────────────────────────────────────────

export const GARDEN_TEMPLATES: GardenTemplate[] = [

  // ── IN-GROUND ──────────────────────────────────────────────────────────────

  {
    id: 'pizza-garden',
    name: 'The Pizza Garden',
    tagline: 'Grow your own pizza ingredients',
    description: 'Everything you need for fresh, homegrown pizza toppings. Tomatoes, herbs, and garlic that taste nothing like anything from a store. Plant directly in the ground and let your soil do the work.',
    category: 'in-ground',
    recommendedSize: '4x8 growing space',
    difficulty: 'beginner',
    season: 'summer',
    emoji: '🍕',
    tags: ['edible', 'herbs', 'italian'],
    plants: [
      { plantId: 'tomato',  plantName: 'Tomato',  quantity: 2, notes: 'Sauce and fresh-eating varieties both work. Roma for sauce, any cherry tomato for snacking.' },
      { plantId: 'basil',   plantName: 'Basil',   quantity: 3, notes: 'Plant near tomatoes — they are natural companions. Pinch flowers to keep leaves coming all summer.' },
      { plantId: 'oregano', plantName: 'Oregano', quantity: 1, notes: 'Perennial in most climates. Harvest just before flowering for peak flavour.' },
      { plantId: 'parsley', plantName: 'Parsley', quantity: 1, notes: 'Flat-leaf (Italian) parsley has the most flavour for cooking.' },
      { plantId: 'garlic',  plantName: 'Garlic',  quantity: 6, notes: 'Plant individual cloves pointed-end up. Harvest when lower leaves yellow. Cure for 2–3 weeks before using.' },
    ],
  },

  {
    id: 'salad-garden',
    name: 'The Salad Garden',
    tagline: 'Fresh greens from your door to your bowl',
    description: 'Cut-and-come-again greens that keep producing all season. Harvest outer leaves and the plant keeps growing — salad on demand. In-ground growing gives these shallow-rooted plants perfect moisture regulation.',
    category: 'in-ground',
    recommendedSize: '4x4 growing space',
    difficulty: 'beginner',
    season: 'spring',
    emoji: '🥗',
    tags: ['edible', 'greens', 'fast-growing'],
    plants: [
      { plantId: 'lettuce',  plantName: 'Lettuce',  quantity: 4, notes: 'Sow in succession every 2–3 weeks for continuous harvest. Loose-leaf varieties are most productive.' },
      { plantId: 'arugula',  plantName: 'Arugula',  quantity: 2, notes: 'Fastest growing green in this mix. Harvest young for a milder flavour.' },
      { plantId: 'spinach',  plantName: 'Spinach',  quantity: 3, notes: 'Bolts in heat — sow in early spring or again in early autumn for best results.' },
      { plantId: 'radish',   plantName: 'Radish',   quantity: 6, notes: 'Ready in under 30 days. Plant at the edges as a fast marker crop while slower greens establish.' },
      { plantId: 'chives',   plantName: 'Chives',   quantity: 8, notes: 'Use like green onions — snip with scissors from the base. They regrow quickly and have beautiful edible flowers.' },
    ],
  },

  {
    id: 'tea-garden',
    name: 'The Tea Garden',
    tagline: 'Grow your own calming cup',
    description: 'A collection of herbs for fresh and dried teas. Step outside, snip a handful, steep for five minutes. Many of these plants are vigorous self-seeders that thrive when connected to living soil.',
    category: 'in-ground',
    recommendedSize: '4x4 growing space',
    difficulty: 'beginner',
    season: 'spring',
    emoji: '🍵',
    tags: ['herbs', 'medicinal', 'calming'],
    categoryNote: 'Note: mint spreads vigorously in-ground. Consider planting it in a sunken pot within the bed to contain its roots.',
    plants: [
      { plantId: 'chamomile',   plantName: 'Chamomile',   quantity: 2, notes: 'Harvest flower heads when fully open. Dry in a single layer in a warm spot for a week.' },
      { plantId: 'mint',        plantName: 'Mint',        quantity: 1, notes: 'Plant in a sunken pot to contain spreading. Use fresh or dry for tea — peppermint and spearmint both work beautifully.' },
      { plantId: 'lemongrass',  plantName: 'Lemongrass',  quantity: 1, notes: 'Citrusy and calming. Use the lower stalk and leaves steeped in boiling water. Hardy in zones 9+; treat as annual elsewhere.' },
      { plantId: 'lavender',    plantName: 'Lavender',    quantity: 2, notes: 'Harvest spikes just before fully open. Dry upside down in small bunches. Use sparingly — potent flavour.' },
      { plantId: 'echinacea',   plantName: 'Echinacea',   quantity: 1, notes: 'Perennial. Roots, flowers, and leaves all used traditionally. Will bloom from year two onwards.' },
    ],
  },

  {
    id: 'cutting-garden',
    name: 'The Cutting Garden',
    tagline: 'Grow your own bouquets all summer',
    description: 'A riot of colour from early summer through frost. These flowers evolved to grow in the ground and their root systems benefit from connecting to the broader soil ecosystem and its beneficial fungi.',
    category: 'in-ground',
    recommendedSize: '4x8 growing space',
    difficulty: 'beginner',
    season: 'summer',
    emoji: '💐',
    tags: ['flowers', 'pollinator', 'cutting'],
    plants: [
      { plantId: 'sunflower',  plantName: 'Sunflower',  quantity: 4, notes: 'Plant in a back row — they get tall. Cut stems with a sharp knife in the morning when flowers first open.' },
      { plantId: 'zinnia',     plantName: 'Zinnia',     quantity: 6, notes: 'The more you cut, the more they bloom. Harvest when fully open. Direct sow after last frost.' },
      { plantId: 'marigold',   plantName: 'Marigold',   quantity: 4, notes: 'Long-stemmed varieties like African marigolds make excellent cut flowers. Also deters nematodes in the soil.' },
      { plantId: 'calendula',  plantName: 'Calendula',  quantity: 4, notes: 'Flowers from early summer. Deadhead regularly. Petals are edible — add to salads.' },
      { plantId: 'cosmos',     plantName: 'Cosmos',     quantity: 6, notes: 'Feathery and delicate. Self-seeds freely. Harvest just as buds begin to open for longest vase life.' },
    ],
  },

  {
    id: 'pollinator-border',
    name: 'The Pollinator Border',
    tagline: 'Feed the bees. Feed your garden.',
    description: 'A living insect corridor that attracts beneficial pollinators and predatory insects. Plant directly in the ground alongside your vegetables — in-ground plants connect to the mycorrhizal networks that help them communicate with and support each other.',
    category: 'in-ground',
    recommendedSize: '4x8 growing space',
    difficulty: 'beginner',
    season: 'summer',
    emoji: '🐝',
    tags: ['flowers', 'pollinator', 'ecological'],
    plants: [
      { plantId: 'borage',     plantName: 'Borage',     quantity: 3, notes: 'One of the best bee plants available. Blue star-shaped flowers. Self-seeds year after year once established.' },
      { plantId: 'marigold',   plantName: 'Marigold',   quantity: 4, notes: 'Attracts hoverflies whose larvae eat aphids. Plant liberally throughout your growing space.' },
      { plantId: 'lavender',   plantName: 'Lavender',   quantity: 2, notes: 'A magnet for bees and butterflies. Perennial. Plant in the sunniest, best-drained spot in the border.' },
      { plantId: 'sunflower',  plantName: 'Sunflower',  quantity: 3, notes: 'Provides pollen and nectar for many insect species. Leave seed heads standing in autumn for birds.' },
      { plantId: 'calendula',  plantName: 'Calendula',  quantity: 4, notes: 'Long-flowering companion plant. Sticky stems trap aphids away from vegetables.' },
    ],
  },

  {
    id: 'beginner-kitchen-garden',
    name: 'The Beginner Kitchen Garden',
    tagline: 'Your first food garden, done right',
    description: 'The most forgiving, productive, and satisfying combination for a first-time food gardener. Plant directly in prepared ground soil — no raised bed needed. Healthy in-ground soil is a living ecosystem that supports your plants naturally.',
    category: 'in-ground',
    recommendedSize: '4x8 growing space',
    difficulty: 'beginner',
    season: 'summer',
    emoji: '🥬',
    tags: ['edible', 'beginner', 'vegetables'],
    plants: [
      { plantId: 'tomato',       plantName: 'Tomato',       quantity: 2, notes: 'The most rewarding first vegetable. Stake well and water consistently. A homegrown tomato is worth it every time.' },
      { plantId: 'zucchini',     plantName: 'Zucchini',     quantity: 1, notes: 'One plant will feed a family. Harvest small (6–8 inches) for best flavour. Flowers are edible too.' },
      { plantId: 'green-beans',  plantName: 'Green Beans',  quantity: 6, notes: 'Direct sow in warm soil. Bush beans need no staking and produce in about 55 days.' },
      { plantId: 'basil',        plantName: 'Basil',         quantity: 2, notes: 'Plant near tomatoes. Pinch off flower buds as they appear to keep producing leaves all summer.' },
      { plantId: 'marigold',     plantName: 'Marigold',     quantity: 3, notes: 'Plant at the corners and edges. Deters aphids, whiteflies, and nematodes from your vegetables.' },
    ],
  },

  // ── RAISED BED ─────────────────────────────────────────────────────────────

  {
    id: 'accessible-garden',
    name: 'The Accessible Garden',
    tagline: 'Gardening at any height, any ability',
    description: 'A raised bed garden designed for gardeners with limited mobility, back pain, or who garden from a wheelchair. A bed raised to 24–30 inches eliminates the need to kneel or bend. This is one of the genuine cases where a raised bed earns its place.',
    category: 'raised-bed',
    recommendedSize: '4x4 raised bed, 24–30 inches tall',
    difficulty: 'beginner',
    season: 'summer',
    emoji: '♿',
    tags: ['accessible', 'edible', 'herbs'],
    categoryNote: 'Raised beds are specifically recommended here for accessibility. The extra height makes gardening possible for people who cannot comfortably kneel or bend.',
    plants: [
      { plantId: 'tomato',  plantName: 'Tomato',   quantity: 1, notes: 'Choose a compact or determinate variety for a small raised bed. Tie to a stake fixed to the bed frame.' },
      { plantId: 'basil',   plantName: 'Basil',    quantity: 2, notes: 'Thrives alongside tomatoes. Pinch regularly — easy to do from a seated position.' },
      { plantId: 'lettuce', plantName: 'Lettuce',  quantity: 3, notes: 'Cut-and-come-again varieties give repeated harvests without replanting. No bending required.' },
      { plantId: 'chives',  plantName: 'Chives',   quantity: 4, notes: 'Snip from the base with scissors. Regrows quickly. No kneeling or digging needed once established.' },
      { plantId: 'parsley', plantName: 'Parsley',  quantity: 1, notes: 'Reliable and easy to harvest a few stems at a time. Keep a pair of scissors near the bed.' },
    ],
  },

  {
    id: 'urban-soil-garden',
    name: 'The Urban Starter Garden',
    tagline: 'Garden safely over compacted or unknown urban soil',
    description: 'In urban environments, native soil is sometimes compacted, paved over, or has an unknown history. A raised bed filled with quality compost-rich soil lets you start gardening safely and productively while the ground soil underneath gradually improves.',
    category: 'raised-bed',
    recommendedSize: '4x8 raised bed, 12 inches deep',
    difficulty: 'beginner',
    season: 'summer',
    emoji: '🏙️',
    tags: ['urban', 'edible', 'beginner'],
    categoryNote: 'Raised beds make sense here because urban soils are sometimes compacted or have an unknown history. Over time, plant roots and soil organisms will begin improving the ground below.',
    plants: [
      { plantId: 'tomato',   plantName: 'Tomato',   quantity: 2, notes: 'Rich compost-filled raised beds are ideal for tomatoes. Feed monthly and water consistently.' },
      { plantId: 'basil',    plantName: 'Basil',    quantity: 2, notes: 'Pairs beautifully with tomatoes and thrives in loose, fertile raised bed soil.' },
      { plantId: 'lettuce',  plantName: 'Lettuce',  quantity: 3, notes: 'Fast to harvest. Start eating within 6 weeks. Replace with a second sowing after the first bolts.' },
      { plantId: 'radish',   plantName: 'Radish',   quantity: 4, notes: 'The quickest win in the raised bed. Ready in 3–4 weeks. Good as a confidence-building first harvest.' },
      { plantId: 'marigold', plantName: 'Marigold', quantity: 3, notes: 'Plant at the edges. Brightens the bed and deters common vegetable pests.' },
    ],
  },

  // ── CONTAINER ──────────────────────────────────────────────────────────────

  {
    id: 'sunny-windowsill',
    name: 'The Sunny Windowsill',
    tagline: 'A kitchen herb garden in four small pots',
    description: 'The herbs you reach for every time you cook, growing right on your windowsill. No garden required — just a sunny south-facing window and four small pots.',
    category: 'container',
    recommendedSize: '4 small pots (6 inch)',
    difficulty: 'beginner',
    season: 'year-round',
    emoji: '🪴',
    tags: ['herbs', 'indoor', 'kitchen', 'apartment'],
    plants: [
      { plantId: 'basil',   plantName: 'Basil',   quantity: 1, notes: 'Needs the warmest, sunniest spot. Water when the top inch of soil is dry. Do not let it sit in water.' },
      { plantId: 'chives',  plantName: 'Chives',  quantity: 1, notes: 'Very low maintenance. Snip from the base. Will grow back repeatedly from the same pot.' },
      { plantId: 'parsley', plantName: 'Parsley', quantity: 1, notes: 'Flat-leaf for cooking. Grows slowly at first — give it 6 weeks to establish. Then harvest regularly.' },
      { plantId: 'thyme',   plantName: 'Thyme',   quantity: 1, notes: 'Loves a sunny windowsill and dislikes overwatering. Snip sprigs as needed. Very forgiving.' },
    ],
  },

  {
    id: 'patio-tomato-garden',
    name: 'The Patio Tomato Garden',
    tagline: 'Cherry tomatoes on your balcony all summer',
    description: 'You do not need a yard to grow tomatoes. A large container, a sunny spot, and this combination will give you fresh cherry tomatoes from July through September.',
    category: 'container',
    recommendedSize: '1 large container (15+ gallon) + 2 medium pots',
    difficulty: 'beginner',
    season: 'summer',
    emoji: '🍅',
    tags: ['edible', 'container', 'balcony', 'apartment'],
    plants: [
      { plantId: 'tomato',   plantName: 'Tomato',   quantity: 1, notes: 'Choose a compact cherry variety like Tumbling Tom or Patio. Use the largest container you have — 15+ gallons.' },
      { plantId: 'basil',    plantName: 'Basil',    quantity: 2, notes: 'Plant in the same container or in a medium pot alongside. Companion planting works in containers too.' },
      { plantId: 'marigold', plantName: 'Marigold', quantity: 2, notes: 'French marigolds in a small pot at the base of the tomato. Deters whitefly — a common balcony pest.' },
    ],
  },

  {
    id: 'balcony-salad-bowl',
    name: 'The Balcony Salad Bowl',
    tagline: 'Fresh salad from your balcony railing',
    description: 'Window boxes are perfect for cut-and-come-again salad greens. Harvest a handful and they grow right back. Fits on any balcony railing or narrow ledge.',
    category: 'container',
    recommendedSize: '2–3 window boxes (24 inch)',
    difficulty: 'beginner',
    season: 'spring',
    emoji: '🌿',
    tags: ['edible', 'greens', 'balcony', 'apartment', 'fast-growing'],
    plants: [
      { plantId: 'lettuce', plantName: 'Lettuce', quantity: 3, notes: 'Loose-leaf varieties are best for window boxes. Harvest outer leaves only and the plant regrows.' },
      { plantId: 'arugula', plantName: 'Arugula', quantity: 2, notes: 'Fastest growing of the three. Ready to harvest in 3–4 weeks. Peppery and delicious.' },
      { plantId: 'radish',  plantName: 'Radish',  quantity: 4, notes: 'Tuck in at the edges as a fast bonus crop. Ready in under 30 days.' },
    ],
  },

];

// ── Helpers ───────────────────────────────────────────────────────────────────

export function getTemplateById(id: string): GardenTemplate | undefined {
  return GARDEN_TEMPLATES.find(t => t.id === id);
}

export function getTemplatesByCategory(category: string): GardenTemplate[] {
  if (category === 'all') return GARDEN_TEMPLATES;
  return GARDEN_TEMPLATES.filter(t => t.category === category);
}
